import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/ibm-plex-mono/400.css';
import './styles/tokens.css';
import './styles/index.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { MotionProvider } from './hooks/useMotion';
import { PwaUpdateProvider } from './hooks/usePwaUpdate';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { installKeepFocusedFieldVisible } from './lib/keyboardFocus';
import { installTheme } from './lib/theme';
import { installVzhlad } from './lib/vzhlad';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 8_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

// Vzhľad (tokeny) a režim ešte pred prvým vykreslením, aby stránka neblikla
// v inom. Vzhľad prvý: farba lišty (theme-color) potrebuje oboje.
installVzhlad();
installTheme();

// Pole, do ktorého sa píše, nesmie ostať pod klávesnicou (T-28). Na celú
// appku raz — formulárov s textom je veľa a každý by to musel robiť sám.
installKeepFocusedFieldVisible({
  visualViewport: window.visualViewport,
  document,
  requestAnimationFrame: (callback) => window.requestAnimationFrame(callback),
});

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch((error) => {
      console.error('Service worker registration failed', error);
    });

    /**
     * Povedz workerovi, že sa appka naozaj vykreslila.
     *
     * Worker má núdzový režim pre prípad, že sa appka nenaštartuje a nemá sa
     * teda koho spýtať na súhlas s aktualizáciou. Bez tejto správy si ho
     * spúšťal aj vtedy, keď bolo všetko v poriadku — a aktualizácia sa
     * nainštalovala sama uprostred práce. Toto je jediný spoľahlivý dôkaz, že
     * sa pýtať má koho.
     *
     * Posiela sa až po vykreslení: keby to bolo skôr, potvrdzovalo by sa niečo,
     * čo sa ešte nestalo.
     */
    const confirmBoot = () => {
      navigator.serviceWorker.ready
        .then((registration) => {
          (registration.active ?? navigator.serviceWorker.controller)?.postMessage({
            type: 'BOOT_OK',
          });
        })
        .catch(() => {
          // Bez potvrdenia sa worker zachová opatrne, takže tu netreba nič robiť.
        });
    };
    requestAnimationFrame(() => requestAnimationFrame(confirmBoot));
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MotionProvider>
      <PwaUpdateProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BrowserRouter>
              {/* Vnútri routera, aby chyba jednej obrazovky nezhasla celú
                  appku aj s navigáciou — a aby "Skúsiť znova" znamenalo
                  naozaj znova vykresliť, nie znova načítať stránku. */}
              <ErrorBoundary>
                <App />
              </ErrorBoundary>
            </BrowserRouter>
          </AuthProvider>
          <Toaster
            position="top-center"
            toastOptions={{
              className: 'ww-toast',
              // Háčik v ikone má farbu plochy toastu (tokeny) — navy háčik
              // by na papieri Ateliéru vyzeral ako nálepka.
              success: { iconTheme: { primary: '#34c759', secondary: 'rgb(var(--ww-surface-2))' } },
              error: { iconTheme: { primary: '#ff453a', secondary: 'rgb(var(--ww-surface-2))' } },
            }}
          />
        </QueryClientProvider>
      </PwaUpdateProvider>
    </MotionProvider>
  </StrictMode>,
);
